import React, { createContext, useContext, useEffect, useState } from 'react'

type Theme = 'light' | 'dark' | 'black'

interface ThemeContextType {
  theme: Theme
  toggleTheme: () => void
  isDark: boolean
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    const saved = localStorage.getItem('theme')
    return (saved as Theme) || 'light'
  })

  useEffect(() => {
    localStorage.setItem('theme', theme)
    // Remove all theme classes first
    document.documentElement.classList.remove('dark', 'black')
    
    // Add the current theme class
    if (theme === 'dark' || theme === 'black') {
      document.documentElement.classList.add('dark')
    }
    if (theme === 'black') {
      document.documentElement.classList.add('black')
    }
  }, [theme])

  const toggleTheme = () => {
    // Cycle through: light -> dark -> light (black mode removed from UI)
    if (theme === 'light') {
      setTheme('dark')
    } else {
      setTheme('light')
    }
  }

  const value = {
    theme,
    toggleTheme,
    isDark: theme === 'dark' || theme === 'black'
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
} 